import { CareplanPendingAction } from "@/constants/careplan";
import { ProfileStatus } from "@/constants/profile";

export type Option = {
  value: string | number;
  label: string;
};

export const ORIGIN_OPTIONS: Option[] = [
  { label: "Marketing Site", value: "marketing_site" },
  { label: "Cancer Pilot", value: "cancer_pilot" },
  { label: "C-Org", value: "c_org" },
  { label: "Employer Center", value: "employer_center" },
  { label: "Cancer Support Community", value: "cancer_support_community" },
  { label: "Twist Out Cancer", value: "twist_out_cancer" },
  { label: "Cancer Resource Center 1", value: "resource_center_1" },
  { label: "Cancer Resource Center 2", value: "resource_center_2" },
  { label: "Inova", value: "inova" },
  { label: "UVA Health", value: "uva" },
  { label: "Imerman Angels", value: "imerman" },
  { label: "Unite for Her", value: "unite_for_her" },
  { label: "Mass Retirees", value: "mass_retirees" },
  { label: "Cancer Buddy", value: "cancer_buddy" },
  { label: "Realm of Caring", value: "realm_of_caring" },
  { label: "Friend / Family", value: "friend_family" },
  { label: "Northwell Health", value: "northwell_health" },
  { label: "Private Health Management", value: "private_health_management" },
  {
    label: "Memorial Sloan Kettering Cancer Center",
    value: "memorial_sloan_kettering_cancer_center",
  },
  { label: "New England Cancer Specialists", value: "new_england_cancer_specialists" },
  { label: "Mount Sinai", value: "mount_sinai" },
  { label: "GoodRX", value: "goodrx" },
  { label: "Care plan Ad 1", value: "care_plan_ad_1" },
  { label: "Free Care Plan", value: "free_care_plan" },
  { label: "Penn Medicine", value: "penn_medicine" },
  { label: "Memorial Sloan Kettering Cancer Center", value: "mskcc" },
  { label: "Mayo Clinic", value: "mayo_clinic" },
  { label: "Johns Hopkins", value: "johns_hopkins" },
  {
    label: "University of Pittsburgh Medical Center",
    value: "university_pittsburgh_medical_center",
  },
  { label: "Color Health", value: "color_health" },
  { label: "Teen Cancer America", value: "teen_cancer_america" },
  { label: "Other", value: "other" },
  { label: "Jefferson Health", value: "jefferson_health" },
  {
    label: "Green Circle Wellness Clinic / Dr Samiya Hashmi",
    value: "green_circle_wellness_clinic_dr_samiya_hashmi",
  },
];

export const USER_TYPE_OPTIONS: Option[] = [
  { label: "Real", value: 0 },
  { label: "Test", value: 1 },
];

export const USER_STATUS_OPTIONS: Option[] = [
  { label: "Draft", value: ProfileStatus.draft },
  { label: "Pending activation", value: ProfileStatus.pending_activation },
  { label: "Active", value: ProfileStatus.active },
  { label: "Inactive", value: ProfileStatus.inactive },
];

export const TCH_CBD_TYPE_OPTIONS: Option[] = [
  { label: "High THC", value: "high_thc_type" },
  { label: "High CBD", value: "high_cbd_type" },
  { label: "1:1", value: "one_one_type" },
  { label: "CBD", value: "cbd_type" },
  { label: "CBN", value: "cbn" },
  { label: "CBG", value: "cbg" },
  { label: "CBC", value: "cbc" },
  { label: "D8-THC", value: "d8_thc" },
  { label: "CBDA", value: "cbda" },
];

export const PRODUCT_TYPE = {
  beverage_edible: "beverage_edible",
  gummy_edible: "gummy_edible",
  lotion_topical: "lotion_topical",
  tincture: "tincture",
  pill_edible: "pill_edible",
  inhaler: "inhaler",
} as const;

const snakeCaseToTitle = (value: string) => {
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => {
    return char.toUpperCase();
  });
};

export const PRODUCT_TYPE_OPTIONS: Option[] = Object.values(PRODUCT_TYPE).map((value) => {
  return { label: snakeCaseToTitle(value), value };
});

export const PRODUCT_STATUS_OPTIONS: Option[] = [
  { label: "Active", value: "active" },
  { label: "Archived", value: "archived" },
  { label: "Draft", value: "draft" },
];

export const PRODUCT_ROLE_OPTIONS: Option[] = [
  { label: "Foundational", value: "foundational" },
  { label: "Bedtime", value: "bedtime" },
  { label: "Adhoc", value: "adhoc" },
];

export const YES_OR_NO_OPTIONS: Option[] = [
  { label: "Yes", value: 1 },
  { label: "No", value: 0 },
];

export const CAREPLAN_PENDING_ACTION_OPTIONS: Option[] = [
  { label: "Review", value: CareplanPendingAction.review },
  { label: "Approve", value: CareplanPendingAction.approve },
];

export const MESSAGES_STATUS_OPTIONS: Option[] = [
  { label: "Unread", value: "unread" },
  { label: "Read", value: "read" },
  { label: "Inexistent", value: "inexistent" },
];

export const TIMES_OF_USE_LABELS: Record<string, string> = {
  "q.d": "Once daily",
  "b.i.d": "Twice daily",
  "t.i.d": "Three times daily",
  "prn.": "As needed",
  "q.h.s": "At bedtime",
  na: "Not applicable",
} as const;

export const PRODUCT_ROLE = {
  foundational: "foundational",
  bedtime: "bedtime",
  adhoc: "adhoc",
} as const;

export const TIMES_OF_USE = {
  "q.d": "q.d",
  "b.i.d": "b.i.d",
  "t.i.d": "t.i.d",
  "prn.": "prn.",
  "q.h.s": "q.h.s",
  na: "na",
} as const;

export const TIMES_OF_USE_BY_ROLE: Record<
  keyof typeof PRODUCT_ROLE,
  readonly (keyof typeof TIMES_OF_USE)[]
> = {
  [PRODUCT_ROLE.foundational]: [
    TIMES_OF_USE["q.d"],
    TIMES_OF_USE["b.i.d"],
    TIMES_OF_USE["t.i.d"],
    TIMES_OF_USE.na,
  ],
  [PRODUCT_ROLE.bedtime]: [TIMES_OF_USE["q.h.s"], TIMES_OF_USE.na],
  [PRODUCT_ROLE.adhoc]: [TIMES_OF_USE["prn."], TIMES_OF_USE.na],
};

export const DEFAULT_TIMES_OF_USE_BY_ROLE: Record<
  keyof typeof PRODUCT_ROLE,
  keyof typeof TIMES_OF_USE
> = {
  [PRODUCT_ROLE.foundational]: TIMES_OF_USE_BY_ROLE[PRODUCT_ROLE.foundational][0],
  [PRODUCT_ROLE.bedtime]: TIMES_OF_USE_BY_ROLE[PRODUCT_ROLE.bedtime][0],
  [PRODUCT_ROLE.adhoc]: TIMES_OF_USE_BY_ROLE[PRODUCT_ROLE.adhoc][0],
};

export const DOSAGE = {
  Zero: "0mg",
  Half: "0.5mg",
  One: "1mg",
  TwoPointFive: "2.5mg",
  Five: "5mg",
  SevenPointFive: "7.5mg",
  Ten: "10mg",
  Fifteen: "15mg",
  Twenty: "20mg",
  TwentyFive: "25mg",
  Fifty: "50mg",
  OneHundred: "100mg",
  TwoHundred: "200mg",
} as const;

export const PREFERENCE_LABELS = {
  presentationsToAvoid: "Presentations to Avoid",
  productsToAvoid: "Products to Avoid",
  roleToAvoid: "Roles to Avoid",
} as const;
